import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Autocomplete,
  LinearProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Toast from "../../common/Toast";
import Button from "../../common/Button";
import { getUserDetails, updateUserDetails } from "../../../store/api/users";
import { getTimezoneOnboardedOrganisation } from "../../../store/api/organisation";

// Define interfaces for our data types
interface Country {
  id: string;
  title: string;
  isSelected: boolean;
}

interface Region {
  id: string;
  title: string;
  isSelected: boolean;
}

interface Timezone {
  id: string;
  title: string;
  isSelected: boolean;
}

interface UserDetails {
  fullName: string;
  email: string;
  countries: Country[];
  regions: Region[];
  timezones: Timezone[];
  companyType: string;
  jobTitle: string;
}

interface ErrorState {
  fullName?: string;
  email?: string;
  country?: string;
  region?: string;
  timezone?: string;
  newPassword?: string;
  confirmPassword?: string;
  oldPassword?: string;
  passwordStrength?: string;
}

interface UpdateUserDetailsParams {
  fullName: string;
  email: string;
  locationId: string;
  regionId: string;
  timezoneId: string;
  companyType: string;
  jobTitle: string;
  updatePasswordModel?: {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  };
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  meetsRequirements: boolean;
}

const AccountSettings = () => {
  const queryClient = useQueryClient();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState<Country | null>(null);
  const [region, setRegion] = useState("");
  const [timezone, setTimezone] = useState<Timezone | null>(null);
  const [companyType, setCompanyType] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [status, setStatus] = useState(200);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    label: "",
    color: "",
    meetsRequirements: false,
  });

  const [errors, setErrors] = useState<ErrorState>({
    fullName: "",
    email: "",
    country: "",
    region: "",
    timezone: "",
    newPassword: "",
    confirmPassword: "",
    oldPassword: "",
    passwordStrength: "",
  });

  const { data, isLoading } = useQuery<UserDetails>({
    queryKey: ["userDetails"],
    queryFn: () => getUserDetails(),
  });

  const { data: timeZones } = useQuery({
    queryKey: ["onboardedTimeZoneOrganisation"],
    queryFn: () => getTimezoneOnboardedOrganisation(),
  });

  // Password strength calculation
  const calculatePasswordStrength = (password: string): PasswordStrength => {
    if (!password) {
      return { score: 0, label: "", color: "", meetsRequirements: false };
    }

    let score = 0;
    const checks = {
      length: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };

    // Calculate score
    if (checks.length) score += 25;
    if (checks.uppercase) score += 25;
    if (checks.lowercase) score += 25;
    if (checks.special) score += 25;

    // Determine label, color, and if requirements are met
    let label, color, meetsRequirements;

    if (score < 50) {
      label = "Weak";
      color = "#f44336"; // Red
      meetsRequirements = false;
    } else if (score < 75) {
      label = "Fair";
      color = "#ff9800"; // Orange
      meetsRequirements = false;
    } else if (score < 100) {
      label = "Good";
      color = "#2196f3"; // Blue
      meetsRequirements = false;
    } else {
      label = "Strong";
      color = "#4caf50"; // Green
      meetsRequirements = true;
    }

    // All requirements must be met for it to be considered valid
    meetsRequirements =
      checks.length && checks.uppercase && checks.lowercase && checks.special;

    return { score, label, color, meetsRequirements };
  };

  // Update password strength when new password changes
  useEffect(() => {
    const strength = calculatePasswordStrength(newPassword);
    setPasswordStrength(strength);
  }, [newPassword]);

  useEffect(() => {
    if (!isLoading && data) {
      setFullName(data.fullName || "");
      setEmail(data.email || "");
      const selectedCountry =
        data.countries.find((el) => el.isSelected) || null;
      const selectedTimezone =
        data.timezones.find((el) => el.isSelected) || null;
      setCountry(selectedCountry);
      setRegion(data.regions.find((el) => el.isSelected)?.id || "");
      setTimezone(selectedTimezone);
      setCompanyType(data?.companyType || "");
      setJobTitle(data?.jobTitle || "");
    }
  }, [isLoading, data]);

  const { mutate, isPending } = useMutation({
    mutationFn: updateUserDetails,
    onSuccess: async () => {
      queryClient.refetchQueries({ queryKey: ["userDetails"] });
      setResponseMessage("Details Updated");
      setStatus(200);
      // Clear password fields after successful update
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    },
    onError: (error: any) => {
      // Handle API error response
      let errorMessage = "Error occurred";

      if (error?.response?.data?.title) {
        errorMessage = error.response.data.title;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setResponseMessage(errorMessage);
      setStatus(error?.response?.status || 400);
    },
  });

  const validate = () => {
    const newErrors: ErrorState = {
      fullName: "",
      email: "",
      country: "",
      region: "",
      timezone: "",
      newPassword: "",
      confirmPassword: "",
      oldPassword: "",
      passwordStrength: "",
    };

    // Validate required fields
    if (!fullName) newErrors.fullName = "Full name is required.";
    if (!email) newErrors.email = "Email is required.";
    if (!country) newErrors.country = "Country is required.";
    if (!region) newErrors.region = "Region is required.";
    if (!timezone) newErrors.timezone = "Timezone is required.";

    // Password validation: only validate if user intends to change password
    // (i.e., if new password or confirm password has been entered)
    const intendingToChangePassword = newPassword || confirmPassword;

    if (intendingToChangePassword) {
      if (!oldPassword)
        newErrors.oldPassword = "Old password is required to change password.";
      if (!newPassword) newErrors.newPassword = "New password is required.";
      if (!confirmPassword)
        newErrors.confirmPassword = "Confirm password is required.";
      if (newPassword && confirmPassword && newPassword !== confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match.";
      }
      // Check password strength requirements
      if (newPassword && !passwordStrength.meetsRequirements) {
        newErrors.passwordStrength = "Password does not meet requirements.";
      }
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleSave = () => {
    if (!validate()) return;

    // Ensure all required fields are present
    if (!country?.id || !region || !timezone?.id) {
      return;
    }

    const baseData: Omit<UpdateUserDetailsParams, "updatePasswordModel"> = {
      fullName,
      email,
      locationId: country.id,
      regionId: region,
      timezoneId: timezone.id,
      companyType,
      jobTitle,
    };

    // Only include password update if user is actually trying to change password
    const isChangingPassword = newPassword && confirmPassword;

    const updatedData: UpdateUserDetailsParams = isChangingPassword
      ? {
          ...baseData,
          updatePasswordModel: {
            currentPassword: oldPassword,
            newPassword: newPassword,
            confirmNewPassword: newPassword,
          },
        }
      : baseData;

    mutate(updatedData);
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <section className="sectionCenter">
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Your settings
        </Typography>
        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Account
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name *"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete<Country, false>
                  fullWidth
                  options={data?.countries || []}
                  getOptionLabel={(option) => option.title}
                  value={country}
                  onChange={(_, newValue) => setCountry(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country *"
                      error={!!errors.country}
                      helperText={errors.country}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.region}
                >
                  <InputLabel>Region *</InputLabel>
                  <Select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    label="Region *"
                  >
                    {data?.regions.map((region) => (
                      <MenuItem key={region.id} value={region.id}>
                        {region.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.region && (
                    <Typography color="error">{errors.region}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete<Timezone, false>
                  fullWidth
                  options={data?.timezones || []}
                  getOptionLabel={(option) => option.title}
                  value={timezone}
                  onChange={(_, newValue) => setTimezone(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Timezone *"
                      error={!!errors.timezone}
                      helperText={errors.timezone}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Type"
                  value={companyType}
                  onChange={(e) => setCompanyType(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Job Title"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Update password
            </Typography>

            {/* Password Requirements Note */}
            <Typography
              variant="body2"
              sx={{
                color: errors.passwordStrength ? "#f44336" : "#666",
                fontSize: "0.875rem",
                mb: 2,
                textAlign: "left",
              }}
            >
              Passwords must be more than 6 characters in length, and contain a
              mix of uppercase, lowercase, and special characters.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Old password"
                  type={showPassword ? "text" : "password"}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  variant="outlined"
                  error={!!errors.oldPassword}
                  helperText={errors.oldPassword}
                  // Multiple strategies to prevent auto-fill
                  autoComplete="one-time-code"
                  name="not-current-password"
                  id="not-current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    "data-lpignore": "true", // LastPass ignore
                    "data-form-type": "other", // General form type hint
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={!!errors.newPassword || !!errors.passwordStrength}
                  helperText={
                    errors.newPassword || errors.passwordStrength || ""
                  }
                  variant="outlined"
                  // Prevent browser auto-fill
                  autoComplete="new-password"
                />
              </Grid>

              {/* Password Strength Indicator */}
              {newPassword && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.75rem", color: "#666" }}
                      >
                        Password Strength
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          color: passwordStrength.color,
                          fontWeight: "bold",
                        }}
                      >
                        {passwordStrength.label}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength.score}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: passwordStrength.color,
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  variant="outlined"
                  // Prevent browser auto-fill
                  autoComplete="new-password"
                />
              </Grid>
            </Grid>
            <Box mt={4} display="flex">
              <Button text=" Back" backgroundColor="white" />
              <Button
                text="Save"
                onClick={handleSave}
                disabled={
                  isPending ||
                  (newPassword && !passwordStrength.meetsRequirements) ||
                  (newPassword &&
                    confirmPassword &&
                    newPassword !== confirmPassword) ||
                  false
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Toast message={responseMessage} status={status} />
      </Box>
    </section>
  );
};

export default AccountSettings;
